import type {
  ListingFavoriteCreateMutation,
  ListingFavoriteDeleteMutation,
} from '@gql';

/**
 * What a favorite mutation did. The API answers failures with an error
 * typename and HTTP 200, so a resolved mutation is not a success.
 */
export type FavoriteMutationOutcome =
  | { kind: 'applied'; isFavorited: boolean }
  | { kind: 'signed_out' }
  | { kind: 'rejected'; typename: string };

/**
 * The backend answers a duplicate create with the existing favorite's
 * `Listing`, so any `ValidationErrorCollection` here is a real rejection.
 */
export function outcomeOfCreate(
  data: ListingFavoriteCreateMutation | null | undefined
): FavoriteMutationOutcome {
  const result = data?.listingFavoriteCreate;
  if (result == null) return { kind: 'rejected', typename: 'none' };

  switch (result.__typename) {
    case 'Listing':
      return { kind: 'applied', isFavorited: true };
    case 'UnauthenticatedError':
      return { kind: 'signed_out' };
    case 'NotFoundError':
    case 'ValidationErrorCollection':
      return { kind: 'rejected', typename: result.__typename };
    default: {
      const unhandled: never = result;
      return unhandled;
    }
  }
}

/**
 * `NotFoundError` means there was no favorite to remove, which is the state
 * the guest asked for.
 */
export function outcomeOfDelete(
  data: ListingFavoriteDeleteMutation | null | undefined
): FavoriteMutationOutcome {
  const result = data?.listingFavoriteDelete;
  if (result == null) return { kind: 'rejected', typename: 'none' };

  switch (result.__typename) {
    case 'Listing':
    case 'NotFoundError':
      return { kind: 'applied', isFavorited: false };
    case 'UnauthenticatedError':
      return { kind: 'signed_out' };
    case 'ValidationErrorCollection':
      return { kind: 'rejected', typename: result.__typename };
    default: {
      const unhandled: never = result;
      return unhandled;
    }
  }
}
